import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useStores } from "@/lib/stores";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { money, formatDate } from "@/lib/utils-app";
import { Ban, CheckCircle2, XCircle, ShieldAlert, Trash2 } from "lucide-react";

const REASON_LABEL = {
  desistencia: "Desistência do cliente",
  falta_peca: "Falta de peça",
  outro: "Outro motivo",
};

function CancellationRow({ os, isSuper, isCEO, onAction }) {
  return (
    <tr className="hover:bg-neutral-50" data-testid={`cancel-row-${os.os_code}`}>
      <td className="px-4 py-3 font-mono font-bold">{os.os_code}</td>
      <td className="px-4 py-3">{os.customer_name} {os.customer_surname}<div className="text-xs text-neutral-500">{os.device_brand} {os.device_model}</div></td>
      <td className="px-4 py-3">
        <span className="text-xs uppercase font-bold tracking-wide bg-neutral-100 text-neutral-700 px-2 py-1 rounded">{REASON_LABEL[os.cancellation_reason] || os.cancellation_reason}</span>
        {os.cancellation_note && <div className="text-xs text-neutral-500 mt-1 max-w-sm truncate" title={os.cancellation_note}>{os.cancellation_note}</div>}
      </td>
      <td className="px-4 py-3 text-xs">
        {os.cancellation_requires_refund
          ? <span className="text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded font-semibold">Requer estorno</span>
          : <span className="text-neutral-500">Não pago</span>}
      </td>
      <td className="px-4 py-3 text-neutral-500 text-xs">
        <div>{formatDate(os.cancellation_requested_at)}</div>
        <div className="text-[10px] text-neutral-400">por {os.cancellation_requested_by}</div>
      </td>
      <td className="px-4 py-3 text-right">
        <div className="inline-flex gap-2">
          {os.cancellation_status === "pending" && isSuper && (
            <>
              <Button size="sm" variant="outline" onClick={() => onAction("reject", os)} className="text-amber-700 hover:bg-amber-50" data-testid={`cancel-reject-${os.os_code}`}>
                <XCircle size={12} className="mr-1"/> Reprovar
              </Button>
              <Button size="sm" onClick={() => onAction("approve", os)} className="bg-green-600 hover:bg-green-700 text-white" data-testid={`cancel-approve-${os.os_code}`}>
                <CheckCircle2 size={12} className="mr-1"/> Aprovar
              </Button>
            </>
          )}
          {os.cancellation_status === "approved" && isSuper && (
            <Button size="sm" variant="outline" onClick={() => onAction("delete", os)} className="text-red-600 hover:bg-red-50 border-red-200" data-testid={`cancel-delete-${os.os_code}`}>
              <Trash2 size={12} className="mr-1"/> Excluir OS
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function AdminCancellations() {
  const { user } = useAuth();
  const { activeStore } = useStores();
  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);
  const [rejected, setRejected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOs, setDialogOs] = useState(null);
  const [dialogAction, setDialogAction] = useState(null);
  const [note, setNote] = useState("");
  const isSuper = user?.role === "superadmin";
  const isCEO = isSuper && (user?.tags || []).includes("ceo");

  const load = async () => {
    setLoading(true);
    try {
      const qs = activeStore ? `&store_id=${activeStore}` : "";
      const [p, a, r] = await Promise.all([
        api.get(`/admin/cancellations?status=pending${qs}`),
        api.get(`/admin/cancellations?status=approved${qs}`),
        api.get(`/admin/cancellations?status=rejected${qs}`),
      ]);
      setPending(p.data); setApproved(a.data); setRejected(r.data);
    } catch (e) { toast.error("Falha ao carregar"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [activeStore]);

  const openDialog = (action, os) => { setDialogOs(os); setDialogAction(action); setNote(""); };
  const closeDialog = () => { setDialogOs(null); setDialogAction(null); setNote(""); };

  const submit = async () => {
    try {
      if (dialogAction === "approve") {
        await api.post(`/admin/service-orders/${dialogOs.os_code}/approve-cancellation`, { note });
        toast.success(`Cancelamento aprovado — OS ${dialogOs.os_code}`);
      } else if (dialogAction === "reject") {
        await api.post(`/admin/service-orders/${dialogOs.os_code}/reject-cancellation`, { note });
        toast.success(`Cancelamento reprovado — OS ${dialogOs.os_code}`);
      } else if (dialogAction === "delete") {
        await api.delete(`/admin/service-orders/${dialogOs.os_code}`);
        toast.success(`OS ${dialogOs.os_code} excluída`);
      }
      closeDialog();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const Table = ({ rows, empty }) => (
    <div className="mt-4 bg-white border border-neutral-200 rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 border-b border-neutral-200 text-neutral-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">OS</th>
              <th className="px-4 py-3 text-left font-semibold">Cliente / Aparelho</th>
              <th className="px-4 py-3 text-left font-semibold">Motivo</th>
              <th className="px-4 py-3 text-left font-semibold">Financeiro</th>
              <th className="px-4 py-3 text-left font-semibold">Solicitado</th>
              <th className="px-4 py-3 text-right font-semibold">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {loading && <tr><td colSpan={6} className="p-6 text-center text-neutral-500">Carregando…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-neutral-500">{empty}</td></tr>}
            {!loading && rows.map((os) => <CancellationRow key={os.os_code} os={os} isSuper={isSuper} isCEO={isCEO} onAction={openDialog} />)}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl">
      <div className="text-xs uppercase tracking-[0.25em] font-bold text-neutral-500 mb-3">Operacional</div>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-red-50 text-red-600 rounded-lg grid place-items-center"><Ban size={18} /></div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight">Cancelamentos</h1>
      </div>
      <p className="mt-2 text-sm text-neutral-600 max-w-2xl">Solicitações de cancelamento das OS. Apenas o super-admin pode aprovar ou reprovar.</p>

      {!isSuper && (
        <div className="mt-6 flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900" data-testid="cancel-readonly-notice">
          <ShieldAlert size={16} className="mt-0.5 shrink-0"/>
          <div>Você tem acesso somente-leitura. As aprovações e reprovações são reservadas ao super-admin.</div>
        </div>
      )}

      <Tabs defaultValue="pending" className="mt-6">
        <TabsList data-testid="cancellation-tabs">
          <TabsTrigger value="pending" data-testid="tab-pending">Pendentes ({pending.length})</TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved">Aprovados ({approved.length})</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">Reprovados ({rejected.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="pending"><Table rows={pending} empty="Nenhuma solicitação pendente."/></TabsContent>
        <TabsContent value="approved"><Table rows={approved} empty="Nenhum cancelamento aprovado."/></TabsContent>
        <TabsContent value="rejected"><Table rows={rejected} empty="Nenhuma solicitação reprovada."/></TabsContent>
      </Tabs>

      <AlertDialog open={!!dialogOs} onOpenChange={(v) => !v && closeDialog()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {dialogAction === "approve" && "Aprovar cancelamento"}
              {dialogAction === "reject" && "Reprovar cancelamento"}
              {dialogAction === "delete" && "Excluir OS definitivamente"}
              {" — "}<span className="font-mono">{dialogOs?.os_code}</span>
            </AlertDialogTitle>
            <AlertDialogDescription>
              {dialogAction === "delete"
                ? <>Esta ação é <b>irreversível</b>. A OS, transações de pagamento e histórico serão apagados.</>
                : <>Motivo alegado: <b>{REASON_LABEL[dialogOs?.cancellation_reason]}</b>{dialogOs?.cancellation_requires_refund ? " · requer estorno" : ""}. Escreva uma observação (opcional):</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {dialogAction !== "delete" && (
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Observação para o histórico" data-testid="cancel-note" />
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-dialog-cancel">Cancelar</AlertDialogCancel>
            {(() => {
              let btnClass = "bg-amber-600 hover:bg-amber-700 text-white";
              if (dialogAction === "delete") btnClass = "bg-red-600 hover:bg-red-700 text-white";
              else if (dialogAction === "approve") btnClass = "bg-green-600 hover:bg-green-700 text-white";
              return (
                <AlertDialogAction onClick={submit} className={btnClass} data-testid="cancel-dialog-confirm">
                  {dialogAction === "approve" && "Sim, aprovar"}
                  {dialogAction === "reject" && "Sim, reprovar"}
                  {dialogAction === "delete" && "Sim, excluir"}
                </AlertDialogAction>
              );
            })()}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
